import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Mail, Loader2, Plus, UserPlus, UserCheck } from 'lucide-react';
import { adminApi, AccountResponse } from '../services/api';
import Alert from '../components/Alert';
import Modal from '../components/Modal';
import DataTable from '../components/DataTable';

const AdminAccountsList: React.FC = () => {
    const navigate = useNavigate();
    const [accounts, setAccounts] = useState<AccountResponse[]>([]);
    const [filteredAccounts, setFilteredAccounts] = useState<AccountResponse[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<AccountResponse | null>(null);
    const [inviteEmail, setInviteEmail] = useState<string>('');
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [accountName, setAccountName] = useState<string>('');
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createSuccess, setCreateSuccess] = useState<string | null>(null);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchAccounts();
    }, []);

    useEffect(() => {
        const filtered = accounts.filter(account =>
            account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            account.number.toString().includes(searchTerm)
        );
        setFilteredAccounts(filtered);
        setCurrentPage(1);
    }, [searchTerm, accounts]);

    const fetchAccounts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await adminApi.getAllAccounts();
            setAccounts(response.data || []);
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Não foi possível carregar as contas.';
            setError(friendly);
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenInviteModal = (account: AccountResponse) => {
        setSelectedAccount(account);
        setInviteEmail('');
        setInviteError(null);
        setInviteSuccess(null);
        setInviteModalOpen(true);
    };

    const handleCloseInviteModal = () => {
        setInviteModalOpen(false);
        setSelectedAccount(null);
        setInviteEmail('');
        setInviteError(null);
        setInviteSuccess(null);
    };

    const handleSendInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedAccount) return;

        setInviteError(null);
        setInviteSuccess(null);

        if (!inviteEmail.trim()) {
            setInviteError('O email é obrigatório.');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inviteEmail.trim())) {
            setInviteError('Por favor, insira um email válido.');
            return;
        }

        try {
            setInviteSubmitting(true);
            await adminApi.sendInvite(selectedAccount.id, { email: inviteEmail.trim() });
            setInviteSuccess('Convite enviado com sucesso!');
            setInviteEmail('');
            // Close modal after 1.5 seconds
            setTimeout(() => {
                handleCloseInviteModal();
            }, 1500);
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao enviar o convite.';
            setInviteError(friendly);
        } finally {
            setInviteSubmitting(false);
        }
    };

    const handleOpenCreateModal = () => {
        setCreateModalOpen(true);
        setAccountName('');
        setCreateError(null);
        setCreateSuccess(null);
    };

    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
        setAccountName('');
        setCreateError(null);
        setCreateSuccess(null);
    };

    const handleCreateAccount = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreateError(null);
        setCreateSuccess(null);

        if (!accountName.trim()) {
            setCreateError('O nome é obrigatório.');
            return;
        }

        try {
            setCreateSubmitting(true);
            await adminApi.createAccount({ name: accountName.trim() });
            setCreateSuccess('Conta criada com sucesso!');
            setAccountName('');
            // Refresh accounts list
            await fetchAccounts();
            // Close modal after 1.5 seconds
            setTimeout(() => {
                handleCloseCreateModal();
            }, 1500);
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao criar a conta.';
            setCreateError(friendly);
        } finally {
            setCreateSubmitting(false);
        }
    };

    const handleViewUsers = (accountId: string) => {
        navigate(`/admin/user?accountId=${accountId}`);
    };

    const columns = [
        {
            key: 'number',
            label: 'Número',
            render: (value: number) => <span className="font-medium">{value}</span>,
        },
        {
            key: 'name',
            label: 'Nome',
        },
        {
            key: 'createdAt',
            label: 'Criado em',
            render: (value: string) => <span>{new Date(value).toLocaleString('pt-BR')}</span>,
        },
    ];

    const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredAccounts.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-primary-100 rounded-lg">
                        <Users className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-secondary-900">Contas</h1>
                        <p className="text-secondary-600">Gerencie as contas de sistema</p>
                    </div>
                </div>
                <button
                    onClick={handleOpenCreateModal}
                    className="btn-primary flex items-center"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Cadastrar Nova Conta
                </button>
            </div>

            {error && <Alert type="error" title="Erro ao carregar" message={error} />}

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
            ) : accounts.length === 0 ? (
                <div className="bg-white p-8 rounded-lg border border-secondary-200 text-center">
                    <Users className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
                    <p className="text-secondary-600">Nenhuma conta de sistema cadastrada ainda.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-secondary-200">
                    <DataTable
                        data={paginatedData}
                        columns={columns}
                        searchPlaceholder="Buscar por nome ou número..."
                        searchValue={searchTerm}
                        onSearchChange={setSearchTerm}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        actionsRenderer={(item: AccountResponse) => (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleViewUsers(item.id)}
                                    className="btn-secondary flex items-center gap-2 text-sm"
                                >
                                    <UserCheck className="w-4 h-4" />
                                    Ver Usuários
                                </button>
                                <button
                                    onClick={() => handleOpenInviteModal(item)}
                                    className="btn-secondary flex items-center gap-2 text-sm"
                                >
                                    <Mail className="w-4 h-4" />
                                    Enviar convite
                                </button>
                            </div>
                        )}
                    />
                </div>
            )}

            {/* Invite Modal */}
            <Modal
                isOpen={inviteModalOpen}
                onClose={handleCloseInviteModal}
                title="Enviar Convite"
            >
                {inviteError && (
                    <Alert type="error" title="Erro ao enviar" message={inviteError} />
                )}
                {inviteSuccess && (
                    <Alert type="success" title="Sucesso" message={inviteSuccess} />
                )}

                {selectedAccount && (
                    <form onSubmit={handleSendInvite} className="space-y-4">
                        <div className="mb-4 p-3 bg-secondary-50 rounded-lg">
                            <p className="text-sm text-secondary-600 mb-1">Conta:</p>
                            <p className="font-medium text-secondary-900">{selectedAccount.name} (#{selectedAccount.number})</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">
                                Email *
                            </label>
                            <input
                                type="email"
                                className="input-field w-full"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="usuario@exemplo.com"
                                required
                                autoFocus
                                disabled={inviteSubmitting}
                            />
                            <p className="mt-1 text-xs text-secondary-500">
                                O convite será enviado para este email
                            </p>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={handleCloseInviteModal}
                                className="btn-secondary"
                                disabled={inviteSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex items-center gap-2"
                                disabled={inviteSubmitting}
                            >
                                {inviteSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <Mail className="w-4 h-4" />
                                        Enviar Convite
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Create Account Modal */}
            <Modal
                isOpen={createModalOpen}
                onClose={handleCloseCreateModal}
                title="Cadastrar Nova Conta"
            >
                {createError && (
                    <Alert type="error" title="Erro ao criar" message={createError} />
                )}
                {createSuccess && (
                    <Alert type="success" title="Sucesso" message={createSuccess} />
                )}

                <form onSubmit={handleCreateAccount} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">
                            Nome *
                        </label>
                        <input
                            type="text"
                            className="input-field w-full"
                            value={accountName}
                            onChange={(e) => setAccountName(e.target.value)}
                            placeholder="Nome da conta"
                            required
                            autoFocus
                            disabled={createSubmitting}
                        />
                        <p className="mt-1 text-xs text-secondary-500">
                            O nome será usado para identificar a conta de sistema
                        </p>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={handleCloseCreateModal}
                            className="btn-secondary"
                            disabled={createSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-primary flex items-center gap-2"
                            disabled={createSubmitting}
                        >
                            {createSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Criando...
                                </>
                            ) : (
                                <>
                                    <UserPlus className="w-4 h-4" />
                                    Cadastrar Conta
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default AdminAccountsList;

